import type { Optional, PickByValue } from 'utility-types'
import React, { type FC } from 'react'

import { type AppState, type CybusCoreContainer, type CybusServiceContainer, type ResourceType, Translation } from '../../../domain'

import type {
    BulkDeleteServiceContainersUsecase,
    BulkRestartCoreContainersUsecase,
    BulkRestartServiceContainersUsecase,
    BulkStartCoreContainersUsecase,
    BulkStartServiceContainersUsecase,
    BulkStopCoreContainersUsecase,
    BulkStopServiceContainersUsecase,
    Usecases,
} from '../../../application'

import { DeleteResourceModal, DisableResourceModal, EnableResourceModal, ReenableResourceModal, ServiceModalBody } from '../Resources'
import { useAppUsecases } from '../State'

type Container = Pick<CybusCoreContainer, 'id' | 'name' | 'status'> | Pick<CybusServiceContainer, 'id' | 'name' | 'service' | 'status'>

type Props<Usecase> = Readonly<{
    dimension: ResourceType
    containersSelector: (s: AppState, ids: readonly string[]) => readonly Container[]
    usecase: keyof PickByValue<Usecases, Usecase>
    onConfirmed: VoidFunction
}>

export const StartModal: FC<Optional<Props<BulkStartCoreContainersUsecase | BulkStartServiceContainersUsecase>, 'onConfirmed'>> = ({
    dimension,
    usecase,
    containersSelector,
    onConfirmed,
}) => {
    const { [usecase]: startUsecase } = useAppUsecases()

    return (
        <EnableResourceModal
            title={Translation.START_CONTAINERS}
            subtitle={Translation.START_CONTAINERS_SUBTITLE}
            confirm={Translation.START}
            resourceSelector={containersSelector}
            dimension={dimension}
            onRenderLineContent={ServiceModalBody}
            onCancel={() => startUsecase.selectContainers(null)}
            onConfirm={async () => {
                await startUsecase.start()
                onConfirmed?.()
            }}
        />
    )
}

export const RestartModal: FC<Optional<Props<BulkRestartServiceContainersUsecase | BulkRestartCoreContainersUsecase>, 'onConfirmed'>> = ({
    dimension,
    usecase,
    containersSelector,
    onConfirmed,
}) => {
    const { [usecase]: restartUsecase } = useAppUsecases()

    return (
        <ReenableResourceModal
            title={Translation.RESTART_CONTAINERS}
            subtitle={Translation.RESTART_CONTAINERS_SUBTITLE}
            confirm={Translation.RESTART}
            resourceSelector={containersSelector}
            dimension={dimension}
            onRenderLineContent={ServiceModalBody}
            onCancel={() => restartUsecase.selectContainers(null)}
            onConfirm={async () => {
                await restartUsecase.restart()
                onConfirmed?.()
            }}
        />
    )
}

export const StopModal: FC<Optional<Props<BulkStopServiceContainersUsecase | BulkStopCoreContainersUsecase>, 'onConfirmed'>> = ({
    dimension,
    usecase,
    containersSelector,
    onConfirmed,
}) => {
    const { [usecase]: stopUsecase } = useAppUsecases()

    return (
        <DisableResourceModal
            title={Translation.STOP_CONTAINERS}
            subtitle={Translation.STOP_CONTAINERS_SUBTITLE}
            confirm={Translation.STOP}
            resourceSelector={containersSelector}
            dimension={dimension}
            onRenderLineContent={ServiceModalBody}
            onCancel={() => stopUsecase.selectContainers(null)}
            onConfirm={async () => {
                await stopUsecase.stop()
                onConfirmed?.()
            }}
        />
    )
}

export const DeleteModal: FC<Props<BulkDeleteServiceContainersUsecase>> = ({ dimension, usecase, containersSelector, onConfirmed }) => {
    const { [usecase]: deleteUsecase } = useAppUsecases()

    return (
        <DeleteResourceModal
            title={Translation.DELETE_CONTAINERS}
            subtitle={Translation.DELETE_CONTAINERS_SUBTITLE}
            confirm={Translation.DELETE}
            resourceSelector={containersSelector}
            dimension={dimension}
            onRenderLineContent={ServiceModalBody}
            onCancel={() => deleteUsecase.selectContainers(null)}
            onConfirm={async () => {
                await deleteUsecase.delete()
                onConfirmed()
            }}
        />
    )
}
